import { useCallback, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { getOwner, signOut } from '../api/owner'
import { isNotFound } from '../api/client'
import { LoadingScreen } from '../components/LoadingScreen'
import { OwnerContext } from './context'

/**
 * Zorgt dat er een ingelogde eigenaar is voordat de beheerschermen renderen.
 *
 * Wie niet ingelogd is gaat naar het aanmeldscherm, met de plek waar hij heen
 * wilde in de state. Zo kom je na het aanmelden terug waar je was in plaats van
 * op de startpagina — dat scheelt precies op het moment dat je een link uit een
 * mail opent.
 */
export function OwnerGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()

  const session = useQuery({
    queryKey: ['owner'],
    queryFn: getOwner,

    // Een 401 is hier geen fout maar het antwoord: je bent niet ingelogd.
    retry: (count, error) => !isUnauthorized(error) && !isNotFound(error) && count < 2,
  })

  const leave = useCallback(async () => {
    await signOut()
    queryClient.clear()

    // Zelf navigeren en niet wachten tot het legen van de cache de gate
    // opnieuw laat kijken. Dat gebeurde niet betrouwbaar: de sessie was weg
    // maar het scherm bleef staan, en dan lijkt uitloggen niets te doen.
    navigate('/beheer/aanmelden', { replace: true })
  }, [navigate, queryClient])

  if (session.isPending) {
    return <LoadingScreen label="Even kijken wie je bent…" />
  }

  if (!session.data) {
    return <Navigate to="/beheer/aanmelden" replace state={{ from: location.pathname }} />
  }

  return (
    <OwnerContext.Provider value={{ owner: session.data, signOut: leave }}>
      {children}
    </OwnerContext.Provider>
  )
}

function isUnauthorized(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 401
}
